import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNotification } from "@/shared/hooks/useNotification";
import {
  useCreateProductionDiscard,
  useGreenhouseDiscardAvailability,
} from "../../hooks/useProductionDiscard";
import { DISCARD_REASONS } from "../../services/productionDiscardApi";
import { getErrorMessage } from "@/shared/utils/errorHandler";
interface MarkForDiscardDialogProps {
  instruction: {
    id: string;
    instructionNumber?: string;
    cropTypeName?: string;
    variety?: string;
    quantityGerminated?: number;
    quantityToSow: number;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const MarkForDiscardDialog = ({
  instruction,
  open,
  onOpenChange,
  onSuccess,
}: MarkForDiscardDialogProps) => {
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("3"); // Default: Overgrown
  const [reasonDetails, setReasonDetails] = useState("");
  const [disposalMethod, setDisposalMethod] = useState("");

  const createMutation = useCreateProductionDiscard();
  const availabilityQuery = useGreenhouseDiscardAvailability(instruction.id, open);
  const notify = useNotification();

  const availability = availabilityQuery.data;
  const maxQty = availability?.discardableQuantity ?? 0;
  const parsedQuantity = Number(quantity);
  const isQuantityValid =
    quantity.trim() !== "" &&
    Number.isFinite(parsedQuantity) &&
    parsedQuantity > 0 &&
    parsedQuantity <= maxQty;

  const handleClose = () => {
    setQuantity("");
    setReason("3");
    setReasonDetails("");
    setDisposalMethod("");
    onOpenChange(false);
  };

  const handleSubmit = () => {
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      notify.error("Enter a quantity greater than zero");
      return;
    }
    if (!availability) {
      notify.error("Current greenhouse stock is unavailable");
      return;
    }
    if (parsedQuantity > maxQty) {
      notify.error(
        `Cannot discard ${parsedQuantity} trays. ${maxQty} trays are currently available to discard.`
      );
      return;
    }

    createMutation.mutate(
      {
        sowingInstructionId: instruction.id,
        quantityToDiscard: parsedQuantity,
        reason: parseInt(reason),
        reasonDetails: reasonDetails.trim() || undefined,
        requiresApproval: true,
        greenhouseBedIds: [],
        disposalMethod: disposalMethod.trim() || undefined,
      },
      {
        onSuccess: () => {
          notify.success("Discard request submitted — pending manager approval");
          handleClose();
          onSuccess();
        },
        onError: (error: unknown) => {
          notify.error(getErrorMessage(error) || "Failed to submit discard request");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Mark for Discard</DialogTitle>
          <DialogDescription>
            {instruction.instructionNumber} — {instruction.cropTypeName}{" "}
            {instruction.variety ? `(${instruction.variety})` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {availabilityQuery.isLoading && (
            <p className="text-sm text-muted-foreground">Loading current stock...</p>
          )}
          {availabilityQuery.isError && (
            <p className="text-sm text-destructive">
              Current stock quantities could not be loaded. Discard submission is disabled.
            </p>
          )}
          {availability && (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 border-y py-3 text-sm">
              <dt className="text-muted-foreground">Current physical stock</dt>
              <dd className="text-right font-medium">{availability.physicalQuantity} trays</dd>
              <dt className="text-muted-foreground">Reserved</dt>
              <dd className="text-right">{availability.reservedQuantity} trays</dd>
              <dt className="text-muted-foreground">Filling reserve</dt>
              <dd className="text-right">{availability.fillingReserve} trays</dd>
              {availability.pendingDiscardQuantity > 0 && (
                <>
                  <dt className="text-muted-foreground">Pending discard</dt>
                  <dd className="text-right">{availability.pendingDiscardQuantity} trays</dd>
                </>
              )}
              <dt className="font-medium">Available to discard</dt>
              <dd className="text-right font-semibold">{availability.discardableQuantity} trays</dd>
            </dl>
          )}

          <Field>
            <FieldLabel htmlFor="qty">
              Quantity to Discard <span className="text-destructive">*</span>
            </FieldLabel>
            <Input
              id="qty"
              type="number"
              step="0.01"
              min="0.01"
              max={maxQty}
              placeholder={availability ? `Max ${maxQty}` : "Loading current stock"}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              disabled={createMutation.isPending || !availability || maxQty <= 0}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="reason">
              Reason <span className="text-destructive">*</span>
            </FieldLabel>
            <Select
              value={reason}
              onValueChange={setReason}
              disabled={createMutation.isPending}
            >
              <SelectTrigger id="reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DISCARD_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value.toString()}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel htmlFor="reasonDetails">Details / Observations</FieldLabel>
            <Textarea
              id="reasonDetails"
              rows={3}
              placeholder="Describe the condition, e.g. stems elongated beyond acceptable length"
              value={reasonDetails}
              onChange={(e) => setReasonDetails(e.target.value)}
              disabled={createMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="disposal">Disposal Method</FieldLabel>
            <Input
              id="disposal"
              placeholder="e.g. Composting, Incineration"
              value={disposalMethod}
              onChange={(e) => setDisposalMethod(e.target.value)}
              disabled={createMutation.isPending}
            />
          </Field>

          <p className="text-xs text-muted-foreground">
            This request will be sent to the Farm Manager for approval before stock is reduced.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={createMutation.isPending || !availability || !isQuantityValid}
          >
            {createMutation.isPending ? "Submitting..." : "Submit for Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
